/**
 * Tradução de códigos do portal para rótulo/classe de UI. `situacaoCodigo` é o estado do
 * protocolo NO PORTAL — um eixo diferente do status interno do quadro kanban (que vem do
 * backend e não pode ser fixado em código). Estas funções eram mantidas soltas em
 * `dashboard-servicos.ts` e em `kanban-protocolos.ts`; ficam aqui para os dois consumirem a
 * mesma implementação em vez de manter cópias que divergem com o tempo.
 */

/** Classe CSS correspondente à situação do protocolo no portal, para colorir o selo. */
export function situacaoClasse(codigo?: string): string {
    switch ((codigo ?? '').trim().toUpperCase()) {
        case 'A':
            return 'ativo';
        case 'T':
            return 'tramitado';
        case 'P':
            return 'pendencia';
        case 'C':
            return 'cancelado';
        case 'E':
            return 'encerrado';
        default:
            return 'indefinido';
    }
}

/**
 * Rótulo da situação do protocolo no portal: Aberto / Tramitado / Pendência / Cancelado / Encerrado.
 *
 * O código `A` é exibido como "Aberto", e não "Ativo": para o analista o protocolo está aberto
 * (ninguém começou a atender), e "ativo" se confundia com a situação do MÉDICO, que aparece em
 * outros pontos da mesma tela. A classe CSS continua `ativo` — ela nomeia a família de cor do
 * selo, não o texto, e renomeá-la não muda nada para quem lê.
 */
export function situacaoRotulo(codigo?: string): string {
    switch ((codigo ?? '').trim().toUpperCase()) {
        case 'A':
            return 'Aberto';
        case 'T':
            return 'Tramitado';
        case 'P':
            return 'Pendência';
        case 'C':
            return 'Cancelado';
        case 'E':
            return 'Encerrado';
        default:
            return 'Não informada';
    }
}

/** C|CADASTRO -> 'Cadastro', R|REGISTRO -> 'Registro'; qualquer outro valor omite a linha (string vazia). */
export function modalidadeInscricaoRotulo(modalidade?: string): string {
    const valor = (modalidade ?? '').trim().toUpperCase();

    switch (valor) {
        case 'C':
        case 'CADASTRO':
            return 'Cadastro';
        case 'R':
        case 'REGISTRO':
            return 'Registro';
        default:
            return '';
    }
}

/**
 * Identificador que o analista lê e repete: `numero/ano` quando o protocolo os tem, senão o código
 * do portal. Os dois lugares que desenham o card de protocolo (quadro e painel de detalhe) precisam
 * cair no MESMO texto — um mostrando `143/2026` e o outro o código do portal seriam, para quem olha,
 * dois protocolos diferentes.
 */
export function identificadorProtocolo(protocolo: { numeroProtocolo?: string; anoProtocolo?: string; portal: string }): string {
    if (protocolo.numeroProtocolo && protocolo.anoProtocolo) {
        return `${protocolo.numeroProtocolo}/${protocolo.anoProtocolo}`;
    }

    return protocolo.portal;
}

/**
 * Formata a data/hora da última tramitação no padrão pt-BR. Vazio quando não há valor; o texto
 * cru quando não é uma data válida. Quem chama decide o texto de fallback para "sem data"
 * (painel de detalhe e card do kanban mostram algo diferente nesse caso).
 */
export function formatarDataTramitacao(valor?: string): string {
    const texto = (valor ?? '').trim();
    if (!texto) {
        return '';
    }

    const data = new Date(texto);
    if (Number.isNaN(data.getTime())) {
        return texto;
    }

    return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    }).format(data);
}

/**
 * Formata uma data PURA (`aaaa-mm-dd`, sem hora — o que `LocalDate` do backend serializa em JSON)
 * no padrão pt-BR `dd/mm/aaaa`, sem nunca passar por `Date`.
 *
 * Por quê: a forma só-data do ISO-8601 é interpretada pelo `new Date(...)` como **UTC** (regra do
 * próprio ECMAScript), e formatá-la depois com `Intl.DateTimeFormat` a renderiza no fuso LOCAL
 * (UTC−3 no Brasil) — o dia exibido retrocede para o anterior e ainda aparece uma hora que a data
 * nunca teve (`"2026-02-20"` virando `"19/02/2026, 21:00"`). Fatiar a string e reordenar os campos
 * evita esse fuso por completo. Mesmo cuidado de `apenasData` em `kanban-filtros.presenter.ts`.
 * NÃO troque isto por `new Date(valor).toLocaleDateString()` nem equivalente — reintroduziria o bug.
 */
export function formatarDataSomenteData(valor?: string): string {
    const texto = (valor ?? '').trim();
    if (!texto) {
        return '';
    }

    const partes = /^(\d{4})-(\d{2})-(\d{2})/.exec(texto);
    if (!partes) {
        return texto;
    }

    const [, ano, mes, dia] = partes;
    return `${dia}/${mes}/${ano}`;
}

/**
 * Mesma família de cor do selo, porém a partir do TEXTO da situação — não do código.
 *
 * O detalhamento do protocolo não recebe `situacaoCodigo`: `ProtocoloResumoConsulta` traz só
 * protocolo/ano/UF/pagamento, e a situação chega como o texto que o portal escreveu. Antes disso o
 * SCSS classificava por substring no atributo `data-situacao`; a lógica passa para cá, ao lado da
 * versão por código, para os dois caminhos pintarem o mesmo selo com a mesma regra.
 *
 * Os grupos replicam os que o CSS já usava: 'APROVADO' é da família de ativo, 'EM ANDAMENTO' da de
 * tramitado, e indeferido/recusado acompanham cancelado por serem desfechos negativos.
 */
export function situacaoClassePorTexto(texto?: string): string {
    const valor = (texto ?? '')
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .trim()
        .toUpperCase();

    if (!valor) {
        return 'indefinido';
    }
    if (valor.includes('TRAMIT') || valor.includes('ANDAM')) {
        return 'tramitado';
    }
    if (valor.includes('PEND') || valor.includes('AGUARD')) {
        return 'pendencia';
    }
    if (valor.includes('CANCEL') || valor.includes('INATIV') || valor.includes('RECUS') || valor.includes('INDEF')) {
        return 'cancelado';
    }
    if (valor.includes('ENCERR') || valor.includes('FINALIZ')) {
        return 'encerrado';
    }
    // Depois de INATIV, senão "INATIVO" cairia aqui pelo "ATIV" que ele contém.
    if (valor.includes('ATIV') || valor.includes('APROV')) {
        return 'ativo';
    }
    return 'indefinido';
}

/**
 * Rótulo do badge de situação no detalhamento.
 *
 * Só a situação DO PROTOCOLO adota o vocabulário do quadro (Aberto/Tramitado/Pendência/...): o
 * mesmo badge é usado por "Situação Médico", "Situação Prestador", "Situação do Consultório" e
 * outros, e traduzir aquilo para o vocabulário de protocolo diria que um médico está "Aberto".
 * O campo do protocolo é o que se chama apenas "Situação" — os demais vêm qualificados.
 *
 * Texto fora das cinco situações do protocolo (INDEFERIDO, APROVADO...) é devolvido como veio:
 * pinta na cor da família, mas mantém a palavra, que carrega informação que os cinco rótulos não têm.
 */
export function rotuloBadgeSituacao(rotuloDoCampo?: string, texto?: string): string {
    const valor = (texto ?? '').trim();
    const campo = (rotuloDoCampo ?? '')
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .trim()
        .toLowerCase();

    if (campo !== 'situacao') {
        return valor;
    }

    const semAcento = valor
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toUpperCase();

    if (semAcento.includes('TRAMIT')) {
        return 'Tramitado';
    }
    if (semAcento.includes('PEND')) {
        return 'Pendência';
    }
    if (semAcento.includes('CANCEL')) {
        return 'Cancelado';
    }
    if (semAcento.includes('ENCERR')) {
        return 'Encerrado';
    }
    if (semAcento.includes('ATIV') && !semAcento.includes('INATIV')) {
        return 'Aberto';
    }
    return valor;
}
